import React, { useCallback, useMemo, useState } from "react";
import { match as Match } from "react-router-dom";
import { toast } from "react-toastify";
import { useIntl } from "react-intl";

import IntlMessages from "../../../../../utils/messages";
import { EnumServiceType } from "../../../../../lib/ground-aws-graphql-core/api/graphql/types";
import { GroundGraphqlContextStore } from "../../../../../lib/ground-aws-graphql-core";
import Table from "../../../../../components/Table";
import ConfirmModal from "../../../../../utils/modal/confirm";
import { Product } from "../../../../../lib/ground-aws-graphql-core/models/Product";
import { getImageUrl } from "../../../../../utils/picture";
import { getTranslation } from "../../../../../utils/translation";
import Dropdown from "../../../../../components/Tailwind/Dropdown";
import images from "../../../../../images";
import {
  EnumPaths,
  getServicesPathByTypology,
  navTo,
} from "../../../../../utils/navigation";
import history from "../../../../../history";
import { TableChangeParams } from "../../../../../components/Table/types";
import Badge from "../../../../../components/Tailwind/Badge";
import { DEFAULT_LIMIT, displayDayDDMMYYYY } from "../../../../../utils/config";

const TABLE_HEADERS = {
  COMMON: {
    PRODUCT: (serviceType: EnumServiceType) =>
      `page.list.products.table.head.product.${serviceType}`,
    CATEGORY: "general.category",
    ACTIONS: "general.actions",
  },
  SERVICE: {
    PROVIDER: "general.provider",
  },
  SPACE: {
    FLEXIBLE: "general.flexible",
    CAPACITY: "general.capacity",
    PRIVATIZATION: "general.privatization.by",
  },
} as const;

const TOAST_MESSAGES = {
  DELETE: {
    SUCCESS: (serviceType: EnumServiceType) =>
      `page.product.delete.product.${serviceType}.success`,
    ERROR: (serviceType: EnumServiceType) =>
      `page.product.delete.product.${serviceType}.error`,
  },
  ORDER: {
    SUCCESS: "page.list.products.product.moved.success",
    ERROR: "page.list.products.product.moved.error",
  },
} as const;

const NAVIGATION_PATHS = {
  getEditPath: (
    serviceType: EnumServiceType,
    productId: string,
    serviceId?: string
  ) => {
    const basePath = `${getServicesPathByTypology(serviceType)}/${
      EnumPaths.PRODUCTS
    }/${productId}/${EnumPaths.EDIT_MODE}`;
    if (serviceType === EnumServiceType.SERVICE && serviceId) {
      return `${getServicesPathByTypology(serviceType)}/${serviceId}/${
        EnumPaths.PRODUCTS
      }/${productId}/${EnumPaths.EDIT_MODE}`;
    }

    return basePath;
  },
  getListPath: (
    serviceType: EnumServiceType,
    cid: string,
    serviceId?: string
  ) => {
    if (serviceType === EnumServiceType.SERVICE && serviceId) {
      return `/${EnumPaths.ROOT}/${EnumPaths.CENTERS}/${cid}/${EnumPaths.SERVICES}/${serviceId}`;
    }

    return `/${EnumPaths.ROOT}/${EnumPaths.CENTERS}/${cid}/${EnumPaths.SPACES}`;
  },
} as const;

interface Props {
  loading: boolean;
  setLoading: (loading: boolean) => void;
  serviceType: EnumServiceType;
  onChange: (params: TableChangeParams) => void;
  match: Match<{ cid: string; id: string }>;
  initialParams?: TableChangeParams | null;
  serviceId?: string;
}

interface DropdownAction {
  id: string;
  label: string;
  icon: string;
  onClick: () => void;
}

interface ProductCellData {
  id: string;
  rowElements: Array<{
    element: React.ReactNode;
    onCellClick?: () => void;
  }>;
}

const ProductImage: React.FC<{
  productName: string;
  picture: string | null;
  enabled: boolean;
}> = ({ productName, picture, enabled }) => (
  <span className="inline-block relative">
    <img
      alt={productName}
      src={getImageUrl(null, picture)}
      className="list-thumbnail w-12 h-8 object-contain rounded-4 border-0"
    />
    <span
      className={`absolute bottom-0 right-0 block h-2 w-2 rounded-full ${
        enabled ? "bg-green-400" : "bg-red-400"
      }`}
    />
  </span>
);

const ProductInfo: React.FC<{
  id: string;
  productName: string;
  sku: string;
  isOption: boolean;
  inCatalog: boolean;
}> = ({ id, productName, sku, isOption, inCatalog }) => (
  <div className="pl-2">
    <span data-cy={`product-name-${id}`} className="block leading-4">
      {productName}
    </span>
    <span className="block text-ground-gray-100">#{sku}</span>
    {(isOption || inCatalog) && (
      <span className="block text-ground-gray-100">
        {isOption && (
          <Badge
            label="page.product.as_option"
            bgClassName="bg-ground-blue-200 mr-1"
          />
        )}
        {inCatalog && (
          <Badge
            label="page.product.in_catalog"
            bgClassName="bg-ground-blue-200"
          />
        )}
      </span>
    )}
  </div>
);

const PrivatizationCell: React.FC<{
  privatizations: Product["privatizations"];
  centerTimezone: string;
}> = ({ privatizations, centerTimezone }) => {
  if (!privatizations?.[0]) return null;

  const privatization = privatizations[0];

  return (
    <div>
      <span>{privatization.enterprise.name}</span>
      <span className="text-gray-600 text-xs">
        {` > ${displayDayDDMMYYYY(privatization.end, centerTimezone)}`}
      </span>
    </div>
  );
};

const ProductsTable = ({
  loading,
  setLoading,
  serviceType,
  onChange,
  initialParams,
  match,
  serviceId,
}: Props): JSX.Element => {
  const intl = useIntl();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);

  const items = GroundGraphqlContextStore.useStoreState(
    state => state.product.products.items
  );
  const total = GroundGraphqlContextStore.useStoreState(
    state => state.product.products.total
  );
  const center = GroundGraphqlContextStore.useStoreState(
    state => state.center.center
  );
  const centerTimezone = GroundGraphqlContextStore.useStoreState(
    state => state.center.centerTimezone
  );

  const deleteProductAction = GroundGraphqlContextStore.useStoreActions(
    actions => actions.product.deleteProduct
  );
  const updateProductOrder = GroundGraphqlContextStore.useStoreActions(
    actions => actions.product.updateProductOrder
  );

  const productsTableHead = useMemo(() => {
    const headers = [
      TABLE_HEADERS.COMMON.PRODUCT(serviceType),
      TABLE_HEADERS.COMMON.CATEGORY,
      TABLE_HEADERS.COMMON.ACTIONS,
    ];

    if (serviceType === EnumServiceType.SERVICE) {
      headers.splice(2, 0, TABLE_HEADERS.SERVICE.PROVIDER);
    }

    if (serviceType === EnumServiceType.SPACE) {
      headers.splice(
        2,
        0,
        TABLE_HEADERS.SPACE.FLEXIBLE,
        TABLE_HEADERS.SPACE.CAPACITY,
        TABLE_HEADERS.SPACE.PRIVATIZATION
      );
    }

    return headers;
  }, [serviceType]);

  const handleEditProduct = useCallback(
    (productId: string) => {
      const { cid, id: serviceId } = match.params;
      const path = NAVIGATION_PATHS.getEditPath(
        serviceType,
        productId,
        serviceId
      );
      history.push(navTo(path, cid));
    },
    [match.params, serviceType]
  );

  const handleDelete = useCallback(async () => {
    if (!center?.operator?.id || !productToDelete) return;

    setLoading(true);
    setShowDeleteModal(false);

    try {
      await deleteProductAction({ product: { id: productToDelete.id } });

      const { cid, id: serviceId } = match.params;
      const url = NAVIGATION_PATHS.getListPath(serviceType, cid, serviceId);
      history.push(url);

      toast(
        intl.formatMessage({ id: TOAST_MESSAGES.DELETE.SUCCESS(serviceType) }),
        { type: "success" }
      );
    } catch (error) {
      console.error("Error deleting product:", error);
      toast(
        intl.formatMessage({ id: TOAST_MESSAGES.DELETE.ERROR(serviceType) }),
        { type: "error" }
      );
    } finally {
      setLoading(false);
    }
  }, [
    center,
    productToDelete,
    match.params,
    serviceType,
    deleteProductAction,
    intl,
    setLoading,
  ]);

  const handleOrderSpace = useCallback(
    async (id: string, beforeId?: string, afterId?: string) => {
      const pageIndex = initialParams?.pageIndex || 0;
      const limit = initialParams?.limit || DEFAULT_LIMIT;
      try {
        await updateProductOrder({
          id,
          buildingId: match.params.cid,
          serviceType,
          serviceId,
          beforeId,
          afterId,
          from: pageIndex * limit,
          limit,
        });

        toast(intl.formatMessage({ id: TOAST_MESSAGES.ORDER.SUCCESS }), {
          type: "success",
        });
      } catch (error) {
        console.error("Error updating product order:", error);
        toast(intl.formatMessage({ id: TOAST_MESSAGES.ORDER.ERROR }), {
          type: "error",
        });
      }
    },
    [updateProductOrder, match.params.cid, intl, initialParams]
  );

  const handleDeleteClick = useCallback((product: Product) => {
    setProductToDelete(product);
    setShowDeleteModal(true);
  }, []);

  const createDropdownActions = useCallback(
    (product: Product): DropdownAction[] => [
      {
        id: "edit",
        label: "general.edit",
        icon: images.edit,
        onClick: () => handleEditProduct(product.id),
      },
      {
        id: "delete",
        label: "general.delete",
        icon: images.deleteIcon,
        onClick: () => handleDeleteClick(product),
      },
    ],
    [handleEditProduct, handleDeleteClick]
  );

  const renderProductCell = useCallback(
    (product: Product): ProductCellData => {
      const {
        id,
        name,
        pictures,
        enabled,
        sku,
        flexible,
        capacity,
        privatizations,
        provider,
        isOption,
        inCatalog,
      } = product;

      const productName = getTranslation(name);
      const picture = pictures?.[0] ?? null;
      const dropdownActions = createDropdownActions(product);

      const cells: React.ReactNode[] = [
        <div className="flex flex-row items-center" key="product">
          <ProductImage
            productName={productName}
            picture={picture}
            enabled={enabled}
          />
          <ProductInfo
            id={id}
            productName={productName}
            sku={sku}
            isOption={isOption}
            inCatalog={inCatalog}
          />
        </div>,
        <span key="category">{getTranslation(product.category?.name)}</span>,
        <Dropdown
          key="actions"
          values={dropdownActions}
          dataCy={`product-dropdown-${id}`}
        />,
      ];

      if (serviceType === EnumServiceType.SERVICE) {
        cells.splice(
          2,
          0,
          <span key="provider">{getTranslation(provider?.name)}</span>
        );
      }

      if (serviceType === EnumServiceType.SPACE) {
        cells.splice(
          2,
          0,
          <div key="flexible" className="flex justify-center">
            {flexible && <img src={images.checked} alt="flexible" />}
          </div>,
          <IntlMessages
            key="capacity"
            id="general.capacity.label"
            values={{ capacity }}
          />,
          <PrivatizationCell
            key="privatization"
            privatizations={privatizations}
            centerTimezone={centerTimezone}
          />
        );
      }

      const rowElements = cells.map((cell, index) => {
        const isActionCell = index === cells.length - 1;

        return {
          element: cell,
          onCellClick: isActionCell ? undefined : () => handleEditProduct(id),
          additionalClassName: isActionCell ? "flex items-center" : "",
        };
      });

      return { id, rowElements };
    },
    [serviceType, centerTimezone, createDropdownActions, handleEditProduct]
  );

  const productsTableBody = useMemo(
    () => items.map(renderProductCell),
    [items, renderProductCell]
  );

  return (
    <>
      <Table
        initialParams={initialParams}
        loading={loading}
        setLoading={setLoading}
        head={productsTableHead}
        body={productsTableBody}
        noDataText={`page.list.products.${serviceType}.empty`}
        paginationTotal={total}
        paginationLimit={DEFAULT_LIMIT}
        onChange={onChange}
        handleOrderElement={handleOrderSpace}
        limitChoiceEnabled
      />

      <ConfirmModal
        isOpen={showDeleteModal}
        toggle={() => setShowDeleteModal(!showDeleteModal)}
        onRequestClose={() => setShowDeleteModal(false)}
        handleConfirm={handleDelete}
        content={
          <IntlMessages id={`page.product.delete.product.${serviceType}`} />
        }
      />
    </>
  );
};

export default ProductsTable;
